/**
 * ATW Auth hub — production login for Hub (shared `.atw.net` NextAuth session).
 */

export const PROD_AUTH_HUB_ORIGIN = "https://auth.atw.net";
export const PROD_HUB_ORIGIN = "https://hub.atw.net";

export function isHubProductionHost(hostname = window.location.hostname): boolean {
  return hostname.toLowerCase() === "hub.atw.net";
}

/** Prefer Auth hub SSO on production Hub; keep local password form for stubs. */
export function preferAuthHubSso(): boolean {
  if (typeof window === "undefined") return false;
  if (import.meta.env.VITE_HUB_AUTH_HUB_SSO === "0") return false;
  if (import.meta.env.VITE_HUB_AUTH_HUB_SSO === "1") return true;
  return isHubProductionHost();
}

export function authHubOrigin(): string {
  const fromEnv = (import.meta.env.VITE_AUTH_HUB_ORIGIN as string | undefined)?.replace(/\/$/, "");
  if (fromEnv) return fromEnv;
  return PROD_AUTH_HUB_ORIGIN;
}

/** Sign-in URL that returns to Hub `/login?sso=1` for ticket exchange. */
export function authHubSignInUrlForHub(returnPath = "/login?sso=1"): string {
  const callback = new URL(returnPath, window.location.origin).toString();
  const url = new URL("/auth/signin", `${authHubOrigin()}/`);
  url.searchParams.set("callbackUrl", callback);
  return url.toString();
}

/**
 * Clear the shared `.atw.net` NextAuth session, then return to Hub login.
 * Hub's Bearer token is separate; callers must clear local Hub state before navigating.
 */
export function authHubSignOutUrlForHub(returnPath = "/login"): string {
  const callback = new URL(returnPath, window.location.origin).toString();
  const url = new URL("/auth/signout", `${authHubOrigin()}/`);
  url.searchParams.set("callbackUrl", callback);
  return url.toString();
}

/**
 * True when Auth hub still has a NextAuth session cookie for this browser.
 * Uses `/api/hub/sso-ticket` (Hub CORS) instead of `/api/auth/session` (no ACAO on auth.atw.net).
 */
export async function authHubHasSession(): Promise<boolean> {
  try {
    const res = await fetch(`${authHubOrigin()}/api/hub/sso-ticket`, {
      method: "GET",
      credentials: "include",
      cache: "no-store",
    });
    if (res.status === 401) return false;
    if (res.ok) return true;
    // Signed in at Auth but missing Hub grant — still a live Auth session.
    if (res.status === 403) {
      const data = (await res.json().catch(() => ({}))) as { code?: string };
      return data.code === "HUB_ACCESS_DENIED";
    }
    return false;
  } catch {
    return false;
  }
}

export function authHubNoAccessUrl(): string {
  const url = new URL("/auth/no-access", `${authHubOrigin()}/`);
  url.searchParams.set("system", "hub");
  return url.toString();
}

export async function fetchHubSsoTicket(): Promise<{
  ticket: string;
  user: { id?: string; email: string; name?: string };
}> {
  const res = await fetch(`${authHubOrigin()}/api/hub/sso-ticket`, {
    method: "GET",
    credentials: "include",
    cache: "no-store",
  });
  const data = (await res.json().catch(() => ({}))) as {
    ticket?: string;
    user?: { id?: string; email?: string; name?: string };
    error?: string;
    code?: string;
  };
  if (res.status === 403 && data.code === "HUB_ACCESS_DENIED") {
    window.location.replace(authHubNoAccessUrl());
    throw new Error(data.error || "Hub access denied.");
  }
  if (!res.ok || !data.ticket) {
    throw new Error(data.error || "Sign in at ATW Auth first, then return to Hub.");
  }
  return {
    ticket: data.ticket,
    user: {
      id: data.user?.id,
      email: (data.user?.email || "").trim().toLowerCase(),
      name: data.user?.name,
    },
  };
}
